import React, { useState, useEffect, useMemo } from 'react';
import { Button, Table, Space, message, Input, Progress, Tag, Drawer, Typography, Select, InputNumber, AutoComplete, Modal, Spin, List, Switch, Tabs, Tooltip } from 'antd';
import { ClockCircleOutlined, LoadingOutlined, UnorderedListOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import jwt_decode from 'jwt-decode';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
// eslint-disable-next-line no-unused-vars
import AlertSelectionModal from './backtesting/AlertSelectionModal';
import ConfigurationForm from './backtesting/ConfigurationForm';
import ResultsDrawer from './backtesting/ResultsDrawer';
import CeleryStatus from './CeleryStatus';
import EquityCheckModal from './backtesting/EquityCheckModal';
import RunHistoryTable from './backtesting/RunHistoryTable';
import PreparedRunsTable from './backtesting/PreparedRunsTable';
import DataValidationTab from './backtesting/DataValidationTab';
import SelectNowResultsTab from './backtesting/SelectNowResultsTab';
import FactorCorrelationsTab from './backtesting/FactorCorrelationsTab';
import { CLAUSE_COOLDOWN_MODE_OPTIONS } from './backtesting/utils';

// Custom Hooks
import { useBacktestConfig } from './backtesting/hooks/useBacktestConfig';
import { useBacktestRun } from './backtesting/hooks/useBacktestRun';
import { useBacktestResults } from './backtesting/hooks/useBacktestResults';
import { useClauseManager } from './backtesting/hooks/useClauseManager';

const Backtesting = () => {
  const [loading, setLoading] = useState(false);
  const [startPortfolios, setStartPortfolios] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState('');
  const [newNameError, setNewNameError] = useState(false); // DAS-1769: Highlight input on error
  
  // DAS-1666: Active tab state (portfolios, history, or prepared)
  const [activeTab, setActiveTab] = useState('portfolios');
  
  // DAS-1703: Serial Run state
  const [isSerialRunActive, setIsSerialRunActive] = useState(false);
  const [serialRunProgress, setSerialRunProgress] = useState(null);
  
  // JWT Timer State
  const [tokenTimeLeft, setTokenTimeLeft] = useState(0);
  
  // Details drawer state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState(null);
  const [editName, setEditName] = useState('');
  const [holdingsRows, setHoldingsRows] = useState([]);
  const [cashRows, setCashRows] = useState([]);
  const [tickerOptionsByKey, setTickerOptionsByKey] = useState({});
  
  const currencyOptions = ['EUR','USD','GBP','CHF','JPY'];
  const spinnerIndicator = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  // JWT Timer Logic
  const calculateTokenTimeRemaining = () => {
    const token = localStorage.getItem('token');
    if (!token) return 0;
    try {
      const decoded = jwt_decode(token);
      const exp = decoded.exp * 1000;
      return Math.max(0, exp - Date.now());
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    const updateTimer = () => {
      const remaining = calculateTokenTimeRemaining();
      setTokenTimeLeft(remaining);
      if (remaining < 60000 && remaining > 0) {
        // Near expiry, logic if needed
      }
    };
    updateTimer();
    const interval = setInterval(updateTimer, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, []);

  // API Helper
  const callAction = async (action, payload = {}, options = {}) => {
    const { useLoader = true } = options || {};
    if (useLoader) setLoading(true);
    try {
      const userId = getUserId();
      const res = await api.post('/backtesting', { userId, action, payload });
      return res.data;
    } catch (e) {
      console.error(`[callAction] Error for ${action}:`, e);
      message.error(e?.response?.data?.error || e.message || 'Request failed');
      return null;
    } finally {
      if (useLoader) setLoading(false);
    }
  };

  // DAS-1699: Handler for viewing configuration from run history
  const handleViewConfiguration = async (configurationId) => {
    if (!configurationId) {
      message.warning('No configuration ID provided');
      return;
    }
    await configManager.loadConfiguration(configurationId, startPortfolios, setSelected);
    setConfigOpen(true);
  };

  // DAS-1666: Handler for viewing historical run results
  const handleViewHistoricalResults = (run, resultsData) => {
    // Set run info
    runManager.setRunInfo({
      run_id: run.run_id,
      config_id: run.configuration_id,
      status: run.status,
      config: resultsData.config
    });
    runManager.setRunStatus(run.status);
    
    // Apply results data directly (don't call onResults which expects just a run_id)
    resultsManager.applyResultsData(resultsData, null, { setRunInfo: runManager.setRunInfo });
    
    // Fetch equity check separately
    callAction('equity_check_no_events', { run_id: run.run_id }, { useLoader: false }).then(eqRes => {
      if (eqRes?.success) {
        resultsManager.setEqCheck(eqRes.data);
      }
    });
    
    // Open results drawer
    resultsManager.setResultsOpen(true);
    
    message.success(`Loaded results for Run #${run.run_id}`);
  };

  // --- Hooks Integration ---
  
  // 1. Clause Manager (for config and modal)
  const clauseManager = useClauseManager(callAction);

  // 2. Configuration State
  const configManager = useBacktestConfig(callAction, startPortfolios);
  const { 
    config, setConfig, configOpen, setConfigOpen, 
    selectedConfigId, setSelectedConfigId, savedConfigurations,
    configValidationErrors, setConfigValidationErrors,
    alertModalOpen, setAlertModalOpen, signalSource,
    editingConfigId, setEditingConfigId
  } = configManager;

  // 3. Run Execution
  // DAS-1699: Removed editingConfigId - each run creates its own config snapshot
  const runManager = useBacktestRun(
    callAction, 
    config, 
    selected, 
    configManager.validateConfiguration, 
    setConfigValidationErrors, 
    setConfigOpen
  );
  const { 
    runInfo, runStatus, runPct, runMeta, 
    elapsedLabel, etaLabel, abortRunLoading,
    statusMessage // DAS-1685: Finalization phase feedback
  } = runManager;

  // 4. Results Handling
  const resultsManager = useBacktestResults(callAction, runInfo, runMeta, runManager.setRunInfo);
  
  // Connect Run completion to Results loading
  useEffect(() => {
    runManager.setOnResultsReady((runId) => {
      resultsManager.onResults(runId);
    });
  }, [runManager, resultsManager]);

  // --- Start Portfolio Management ---

  const refreshStartPortfolios = async () => {
    const data = await callAction('list_start_portfolios');
    if (data?.success) {
      const newPortfolios = data.data || [];
      setStartPortfolios(newPortfolios);
      if (selected) {
        const updated = newPortfolios.find(p => p.id === selected.id);
        if (updated) setSelected(updated);
      }
    }
  };

  useEffect(() => {
    refreshStartPortfolios();
    configManager.loadSavedConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DAS-1703: Poll for serial run status
  useEffect(() => {
    const checkSerialRunStatus = async () => {
      const res = await callAction('is_any_batch_running', {}, { useLoader: false });
      if (res?.success) {
        setIsSerialRunActive(res.data.is_running);
        setSerialRunProgress(res.data.is_running ? res.data.progress : null);
      }
    };
    
    // Check immediately
    checkSerialRunStatus();
    
    // Poll every 5 seconds
    const intervalId = setInterval(checkSerialRunStatus, 5000);
    return () => clearInterval(intervalId);
  }, [callAction]);

  const onCreateFromPV = async () => {
    if (!newName) {
      setNewNameError(true); // DAS-1769: Highlight input
      message.error('Enter a portfolio name first');
      return;
    }
    setNewNameError(false);
    const res = await callAction('create_start_portfolio_from_view', { name: newName });
    if (res?.success) {
      message.success('Portfolio created from Portfolio_View');
      setNewName('');
      refreshStartPortfolios();
    }
  };

  const onCreateScratch = async () => {
    if (!newName) {
      setNewNameError(true); // DAS-1769: Highlight input
      message.error('Enter a portfolio name first');
      return;
    }
    setNewNameError(false);
    const res = await callAction('create_start_portfolio_scratch', { name: newName });
    if (res?.success) {
      message.success('Portfolio created');
      setNewName('');
      refreshStartPortfolios();
    }
  };

  const onClone = async () => {
    if (!newName) {
      setNewNameError(true); // DAS-1769: Highlight input
      message.error('Enter a portfolio name first');
      return;
    }
    if (!selected) {
      message.error('Select a portfolio to clone');
      return;
    }
    setNewNameError(false);
    const res = await callAction('clone_start_portfolio', { source_id: selected.id, name: newName });
    if (res?.success) {
      message.success('Portfolio cloned');
      setNewName('');
      refreshStartPortfolios();
    }
  };

  // Portfolio Details Logic
  const openDetails = (record) => {
    setDetailsRecord(record);
    setEditName(record.name);
    
    let hRows = [];
    try {
      hRows = typeof record.holdings === 'string' ? JSON.parse(record.holdings) : record.holdings;
    } catch {}
    setHoldingsRows(hRows.map((h, i) => ({ ...h, key: i })));

    let cRows = [];
    if (record.cash_by_currency) {
      try {
        const cashMap = typeof record.cash_by_currency === 'string' ? JSON.parse(record.cash_by_currency) : record.cash_by_currency;
        cRows = Object.entries(cashMap).map(([curr, amt], i) => ({ key: i, currency: curr, amount: amt }));
      } catch {}
    }
    setCashRows(cRows);

    const initialOptions = {};
    hRows.forEach((h, i) => {
      initialOptions[i] = [{ value: h.ticker }];
    });
    setTickerOptionsByKey(initialOptions);

    setDetailsOpen(true);
  };

  const saveDetails = async () => {
    if (!detailsRecord) return;
    const holdings = holdingsRows.map(({ key, ...rest }) => rest);
    const cashMap = {};
    cashRows.forEach(r => {
      if (r.currency) cashMap[r.currency] = Number(r.amount || 0);
    });

    const payload = {
      id: detailsRecord.id,
      name: editName,
      holdings,
      cash_by_currency: cashMap
    };
    
    const res = await callAction('update_start_portfolio', payload);
    if (res?.success) {
      message.success('Saved');
      setDetailsOpen(false);
      refreshStartPortfolios();
    }
  };

  const addHoldingRow = () => {
    setHoldingsRows((rows) => [...rows, { key: rows.length ? rows[rows.length - 1].key + 1 : 0, ticker: '', quantity: 0, instrument_currency: 'EUR' }]);
  };
  const removeHoldingRow = (key) => {
    setHoldingsRows((rows) => rows.filter(r => r.key !== key));
  };
  const updateHoldingRow = (key, field, value) => {
    setHoldingsRows((rows) => rows.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const searchTickers = async (rowKey, query) => {
    if (!query) return;
    try {
      // Use search_suggestions_esg endpoint (returns "SYMBOL - Name" strings)
      const res = await api.get(`/search_suggestions_esg?query=${encodeURIComponent(query)}`);
      if (res.data && Array.isArray(res.data)) {
        // Response is array of strings like "BTI - British American Tobacco"
        const opts = res.data.map(item => ({ value: item }));
        setTickerOptionsByKey(prev => ({ ...prev, [rowKey]: opts }));
      }
    } catch {
      // ignore
    }
  };

  const addCashRow = () => {
    setCashRows((rows) => [...rows, { key: rows.length ? rows[rows.length - 1].key + 1 : 0, currency: 'EUR', amount: 0 }]);
  };
  const removeCashRow = (key) => {
    setCashRows((rows) => rows.filter(r => r.key !== key));
  };
  const updateCashRow = (key, field, value) => {
    setCashRows((rows) => rows.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  };

  // Calculate portfolio cash for UI
  const portfolioCashEur = useMemo(() => {
    if (!selected || !selected.cash_by_currency) return 0;
    try {
      const cashMap = typeof selected.cash_by_currency === 'string' ? JSON.parse(selected.cash_by_currency) : selected.cash_by_currency;
      return Number(cashMap['EUR'] || 0);
    } catch { return 0; }
  }, [selected]);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Created', dataIndex: 'created_at', render: (val) => new Date(val).toLocaleString() },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openDetails(record)}>Edit</Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Delete portfolio',
                content: 'Are you sure?',
                onOk: async () => {
                  const res = await callAction('delete_start_portfolio', { id: record.id });
                  if (res?.success) {
                    message.success('Deleted');
                    refreshStartPortfolios();
                    if (selected?.id === record.id) setSelected(null);
                  }
                }
              });
            }}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  // Helpers for UI rendering (needed by ConfigurationForm props)
  const renderClauseCooldownRow = ({ type, id, label }) => {
    const key = `${type}:${id}`;
    const override = config.clause_cooldowns[key];
    const hasOverride = !!override;
    
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8, paddingLeft: 12, borderLeft: '2px solid #f0f0f0' }}>
        <div style={{ width: 240, fontSize: 12, fontWeight: 500 }}>{label}</div>
        <Switch
          size="small"
          checked={hasOverride}
          onChange={(chk) => {
            if (chk) configManager.upsertClauseOverride(key, { ...config.clause_cooldown_default });
            else configManager.clearClauseOverride(key);
          }}
        />
        {hasOverride && (
          <>
            <Select
              size="small"
              style={{ width: 160 }}
              value={override.mode}
              options={CLAUSE_COOLDOWN_MODE_OPTIONS}
              onChange={(val) => configManager.upsertClauseOverride(key, { mode: val })}
            />
            <InputNumber
              size="small"
              min={1}
              style={{ width: 100 }}
              value={override.value}
              onChange={(val) => configManager.upsertClauseOverride(key, { value: Number(val || 1) })}
            />
            <span style={{ fontSize: 12 }}>{override.mode === 'count' ? 'trades total' : 'days'}</span>
          </>
        )}
        {!hasOverride && (
          <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
            (Using default: {config.clause_cooldown_default.value} {config.clause_cooldown_default.mode === 'count' ? 'trades' : 'days'})
          </span>
        )}
      </div>
    );
  };

  const renderClausePositionSizingRow = ({ type, id, label, clause }) => {
    const key = `${type}:${id}`;
    const override = config.clause_position_sizing[key];
    // eslint-disable-next-line no-unused-vars
    const hasOverride = override && (override.buy_amount != null || override.sell_percent != null);
    const isSell = clause?.trade_direction === 'sell';
    const isBuy = clause?.trade_direction === 'buy';
    const allowBuy = !isSell;
    const allowSell = !isBuy;

    return (
      <div key={`ps-${key}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8, paddingLeft: 12, borderLeft: '2px solid #f0f0f0' }}>
        <div style={{ width: 240, fontSize: 12, fontWeight: 500 }}>{label}</div>
        {allowBuy && (
          <Space size="small">
            <span style={{ fontSize: 11 }}>Buy (€):</span>
            <InputNumber
              size="small"
              placeholder="Default"
              style={{ width: 100 }}
              value={override?.buy_amount}
              onChange={(val) => {
                if (val === null && override?.sell_percent === null) configManager.clearClausePositionSizing(key);
                else configManager.upsertClausePositionSizing(key, { buy_amount: val });
              }}
            />
          </Space>
        )}
        {allowSell && (
          <Space size="small">
            <span style={{ fontSize: 11 }}>Sell (%):</span>
            <InputNumber
              size="small"
              placeholder="Default"
              style={{ width: 100 }}
              max={100}
              min={0}
              value={override?.sell_percent}
              onChange={(val) => {
                if (val === null && override?.buy_amount === null) configManager.clearClausePositionSizing(key);
                else configManager.upsertClausePositionSizing(key, { sell_percent: val });
              }}
            />
          </Space>
        )}
      </div>
    );
  };

  // Prepare list of clauses/sets for config UI
  const selectedClauseRows = useMemo(() => {
    return (config.backtest_clause_ids || []).map(id => ({
      type: 'clause',
      id,
      label: clauseManager.formatClauseLabel(clauseManager.clauseDefinitionById[id]) || `Clause #${id}`,
      clause: clauseManager.clauseDefinitionById[id]
    }));
  }, [config.backtest_clause_ids, clauseManager]);

  const selectedClauseSetRows = useMemo(() => {
    if (!config.backtest_clause_set_id) return [];
    const set = clauseManager.clauseSets.find(s => s.id === config.backtest_clause_set_id);
    return [{
      type: 'set',
      id: config.backtest_clause_set_id,
      label: set ? (set.name || `Combination #${set.id}`) : `Combination #${config.backtest_clause_set_id}`,
      clause: null
    }];
  }, [config.backtest_clause_set_id, clauseManager.clauseSets]);

  // Run Progress Computation
  const clampedRunPct = Math.min(100, Math.max(0, runPct));

  // Clause Manager Modal - Handlers wrapper
  const handleSaveClauseWrapper = async () => {
    await clauseManager.handleSaveClause();
  };

  const handleSaveClauseCombinationWrapper = async () => {
    const savedItem = await clauseManager.handleSaveClauseCombination();
    if (savedItem && !config.backtest_clause_set_id) {
      setConfig(c => ({ ...c, backtest_clause_set_id: savedItem.id, backtest_clause_mode: 'clause_set' }));
    }
  };

  // Debug: Log Start Run button state
  React.useEffect(() => {
  }, [configManager.isDirty, selected, config.start_portfolio_id, runStatus]);

  return (
    <div style={{ padding: 24 }}>
      {/* Celery Status Banner */}
      <CeleryStatus callAction={callAction} />
      
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space align="center">
          <Typography.Title level={3} style={{ margin: 0 }}>Backtesting</Typography.Title>
          {tokenTimeLeft > 0 && (
            <Tag color={tokenTimeLeft < 60000 ? 'red' : 'default'}>
              Token: {Math.floor(tokenTimeLeft / 60000)}m {Math.floor((tokenTimeLeft % 60000) / 1000)}s
            </Tag>
          )}
        </Space>
        <Space>
          <Button onClick={() => setConfigOpen(true)}>Configuration</Button>
          <Button 
            type="primary" 
            onClick={runManager.onRun} 
            loading={loading} // Use global loading for general actions, or specific
            disabled={!selected || runStatus === 'running' || isSerialRunActive}
            icon={runStatus === 'running' ? <ClockCircleOutlined spin /> : null}
          >
            {isSerialRunActive ? `Serial Run (${serialRunProgress})` : runStatus === 'running' ? 'Running...' : 'Start Run'}
          </Button>
        </Space>
      </div>
      <div style={{ marginBottom: 16 }}>
        {runInfo && (
          <Space>
            <Tag color={runStatus === 'completed' ? 'green' : runStatus === 'failed' ? 'red' : 'blue'}>
              {runStatus ? runStatus.toUpperCase() : 'READY'}
            </Tag>
            <span>Run ID: {runInfo.run_id}</span>
            {elapsedLabel && (
              <Tag color="blue">{elapsedLabel}</Tag>
            )}
            {etaLabel && runStatus === 'running' && (
              <Tag color="purple">{`ETA ~ ${etaLabel}`}</Tag>
            )}
            {runMeta?.total_days > 0 && (
              <Tag color="cyan">
                Day {runMeta.processed_days_estimate || 0} / {runMeta.total_days}
              </Tag>
            )}
            {(runStatus === 'running' || runStatus === 'pending') && (
              <Spin
                spinning
                indicator={spinnerIndicator}
                tip={
                  runStatus === 'pending' 
                    ? 'Initializing...' 
                    : clampedRunPct >= 100 
                      ? (statusMessage || 'Finalizing calculations...') // DAS-1685: Show backend status message
                      : clampedRunPct >= 99 
                        ? 'Almost done...' 
                        : `Processing (${clampedRunPct.toFixed(1)}%)`
                }
              />
            )}
            {/* DAS-1681: Show progress during results loading (after run completes) */}
            {resultsManager.resultsLoadingInfo?.isLoading && (
              <Spin
                spinning
                indicator={spinnerIndicator}
                tip={`${resultsManager.resultsLoadingInfo.stage} (${resultsManager.resultsLoadingInfo.elapsedSeconds}s)`}
              />
            )}
            {(runStatus === 'running' || runStatus === 'pending') && (
              <Button danger size="small" loading={abortRunLoading} onClick={runManager.handleAbortRun} disabled={runStatus === 'aborted'}>
                Stop run
              </Button>
            )}
            <Progress
              style={{ width: 260 }}
              percent={clampedRunPct}
              size="small"
              status={runStatus === 'failed' || runStatus === 'aborted' ? 'exception' : runStatus === 'running' ? 'active' : 'normal'}
              format={(percent) => `${(percent ?? 0).toFixed(1)}%`}
            />
          </Space>
        )}
      </div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="New start portfolio name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            if (newNameError && e.target.value) setNewNameError(false); // DAS-1769: Clear error on typing
          }}
          style={{ width: 260 }}
          status={newNameError ? 'error' : undefined}
        />
        <Button onClick={onCreateScratch} loading={loading}>Create Scratch</Button>
        <Button onClick={onCreateFromPV} loading={loading}>Create from Portfolio_View</Button>
        <Button onClick={onClone} disabled={!selected} loading={loading}>Clone Selected</Button>
        <Select
          placeholder="Load saved configuration..."
          style={{ width: 600 }}
          value={selectedConfigId}
          onChange={async (value) => {
            if (value) {
              setSelectedConfigId(value);
              await configManager.loadConfiguration(value, startPortfolios, setSelected);
              setConfigOpen(true); // DAS-1769: Auto-open drawer when configuration loaded
            } else {
              setSelectedConfigId(null);
            }
          }}
          allowClear
          options={savedConfigurations.map((c) => ({
            value: c.id,
            label: `#${c.id} — ${c.name || 'Unnamed'} (${c.created_at ? new Date(c.created_at).toLocaleDateString() : '?'})`
          }))}
        />
        <Button onClick={refreshStartPortfolios} loading={loading}>Refresh</Button>
      </Space>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'portfolios',
            label: 'Start Portfolios',
            children: (
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={startPortfolios}
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: selected ? [selected.id] : [],
                  onChange: (keys, rows) => {
                    const portfolio = rows?.[0] || null;
                    setSelected(portfolio);
                    // Also update config.start_portfolio_id so it's saved with the configuration
                    if (portfolio) {
                      configManager.setConfig({ ...config, start_portfolio_id: portfolio.id });
                    }
                  }
                }}
                rowClassName={(record) => (selected?.id === record.id ? 'selected-row' : '')}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'history',
            label: 'Run History',
            children: (
              <RunHistoryTable
                callAction={callAction}
                onViewResults={handleViewHistoricalResults}
                onViewConfiguration={handleViewConfiguration}
              />
            )
          },
          {
            key: 'prepared',
            label: (
              <span>
                Prepared Runs
                {isSerialRunActive && <Tag color="blue" style={{ marginLeft: 8 }}>Running</Tag>}
              </span>
            ),
            children: (
              <PreparedRunsTable
                callAction={callAction}
                onViewConfiguration={handleViewConfiguration}
                isSerialRunActive={isSerialRunActive}
                setIsSerialRunActive={setIsSerialRunActive}
              />
            )
          },
          {
            key: 'validation',
            label: 'Data Validation',
            children: <DataValidationTab />
          },
          {
            key: 'selectnow',
            label: 'Select Now Results',
            children: <SelectNowResultsTab callAction={callAction} />
          },
          {
            key: 'correlations',
            label: 'Factor Correlations',
            children: <FactorCorrelationsTab callAction={callAction} />
          }
        ]}
      />
      <Drawer
        title={`Start Portfolio #${detailsRecord?.id || ''}`}
        width={600}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        extra={<Button type="primary" onClick={saveDetails} loading={loading}>Save</Button>}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>Name</Typography.Text>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>
          <div>
            <Space style={{ marginBottom: 8 }}>
              <Typography.Text strong>Holdings</Typography.Text>
              <Button size="small" onClick={addHoldingRow}>Add Row</Button>
            </Space>
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={holdingsRows}
              columns={[
                {
                  title: 'Ticker',
                  dataIndex: 'ticker',
                  render: (v, r) => (
                    <AutoComplete
                      options={tickerOptionsByKey[r.key] || []}
                      value={r.ticker}
                      onSearch={(val) => searchTickers(r.key, val)}
                      onSelect={(val) => updateHoldingRow(r.key, 'ticker', (val || '').split(' - ')[0].toUpperCase())}
                      onChange={(val) => updateHoldingRow(r.key, 'ticker', val)}
                      style={{ width: 160 }}
                      placeholder="ticker or name"
                    />
                  )
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  render: (v, r) => <InputNumber min={0} precision={2} value={r.quantity} onChange={(val) => updateHoldingRow(r.key, 'quantity', val)} />
                },
                {
                  title: 'Currency',
                  dataIndex: 'instrument_currency',
                  render: (v, r) => (
                    <Select
                      value={r.instrument_currency}
                      onChange={(val) => updateHoldingRow(r.key, 'instrument_currency', val)}
                      options={currencyOptions.map(c => ({ label: c, value: c }))}
                      style={{ width: 100 }}
                    />
                  )
                },
                {
                  title: 'Actions',
                  render: (_, r) => <Button danger size="small" onClick={() => removeHoldingRow(r.key)}>Remove</Button>
                }
              ]}
            />
          </div>
          <div>
            <Space style={{ marginBottom: 8 }}>
              <Typography.Text strong>Cash by currency</Typography.Text>
              <Button size="small" onClick={addCashRow}>Add Currency</Button>
            </Space>
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={cashRows}
              columns={[
                {
                  title: 'Currency',
                  dataIndex: 'currency',
                  render: (v, r) => (
                    <Select
                      value={r.currency}
                      onChange={(val) => updateCashRow(r.key, 'currency', val)}
                      options={currencyOptions.map(c => ({ label: c, value: c }))}
                      style={{ width: 100 }}
                    />
                  )
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  render: (v, r) => <InputNumber min={0} precision={2} value={r.amount} onChange={(val) => updateCashRow(r.key, 'amount', val)} />
                },
                {
                  title: 'Actions',
                  render: (_, r) => <Button danger size="small" onClick={() => removeCashRow(r.key)}>Remove</Button>
                }
              ]}
            />
          </div>
        </Space>
      </Drawer>
      <Modal
        title="Manage clauses & combinations"
        open={clauseManager.clauseModalOpen}
        onCancel={clauseManager.handleClauseModalCloseRequest}
        width={1100}
        destroyOnClose
        maskClosable={false}
        footer={
          clauseManager.clauseManagerTab === 'clauses' ? (
            <Space>
              {clauseManager.editingClause && clauseManager.editingClause.id && (
                <Button
                  danger
                  onClick={clauseManager.handleDeleteClause}
                >
                  Delete
                </Button>
              )}
              <Button onClick={clauseManager.handleClauseModalCloseRequest}>Close</Button>
              <Button
                type="primary"
                disabled={!clauseManager.clauseFormDirty || clauseManager.clauseSaveLoading}
                loading={clauseManager.clauseSaveLoading}
                onClick={handleSaveClauseWrapper}
              >
                Save
              </Button>
            </Space>
          ) : (
            <Space>
              {clauseManager.clauseCombinationForm.id && (
                <Button danger onClick={clauseManager.handleDeleteClauseCombination}>
                  Delete combination
                </Button>
              )}
              <Button onClick={clauseManager.handleClauseModalCloseRequest}>Close</Button>
              <Button
                type="primary"
                disabled={!clauseManager.clauseCombinationDirty || clauseManager.clauseCombinationSaveLoading}
                loading={clauseManager.clauseCombinationSaveLoading}
                onClick={handleSaveClauseCombinationWrapper}
              >
                Save combination
              </Button>
            </Space>
          )
        }
      >
        <Tabs
          activeKey={clauseManager.clauseManagerTab}
          onChange={(key) => clauseManager.setClauseManagerTab(key)}
          items={[
            {
              key: 'clauses',
              label: 'Manage Clauses',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Typography.Text strong>Existing clauses</Typography.Text>
                    <Space style={{ width: '100%', marginTop: 4 }} wrap>
                      <Select
                        style={{ flex: 1, minWidth: 260 }}
                        placeholder="Select clause to load"
                        value={clauseManager.editingClause?.id || null}
                        loading={clauseManager.clauseDefinitionsLoading}
                        onDropdownVisibleChange={(open) => {
                          if (open && clauseManager.clauseDefinitions.length === 0) {
                            clauseManager.loadClauseDefinitions();
                          }
                        }}
                        onChange={async (val) => {
                          await clauseManager.loadClauseForEdit(val);
                        }}
                        options={clauseManager.clauseDefinitions.map((cl) => ({
                          value: cl.id,
                          label: clauseManager.formatClauseLabel(cl),
                        }))}
                        allowClear
                      />
                      <Button
                        size="small"
                        onClick={async () => {
                          await clauseManager.ensureClauseDataLoaded();
                          clauseManager.setClauseListDrawerOpen(true);
                        }}
                      >
                        Show all
                      </Button>
                    </Space>
                  </div>
                  <div>
                    <Typography.Text strong>Clause definition</Typography.Text>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Typography.Text>Field / Source table</Typography.Text>
                        <Space>
                          <AutoComplete
                            style={{ width: 220 }}
                            options={clauseManager.clauseFieldOptions}
                            value={clauseManager.clauseForm.clause_field}
                            allowClear
                            filterOption={(inputValue, option) => option?.value?.toLowerCase().includes(String(inputValue || '').toLowerCase())}
                            placeholder="Field"
                            onSelect={(val, option) => {
                              clauseManager.setClauseForm((f) => ({
                                ...f,
                                clause_field: option.field || val,
                                clause_field_sourcetable: option.sourcetable || f.clause_field_sourcetable,
                              }));
                            }}
                            onChange={(val) =>
                              clauseManager.setClauseForm((f) => ({ ...f, clause_field: val }))
                            }
                          />
                          <Input
                            style={{ width: 220 }}
                            placeholder="Source table"
                            value={clauseManager.clauseForm.clause_field_sourcetable}
                            onChange={(e) =>
                              clauseManager.setClauseForm((f) => ({ ...f, clause_field_sourcetable: e.target.value }))
                            }
                          />
                        </Space>
                      </div>
                      <div>
                        <Typography.Text>Scope</Typography.Text>
                        <Select
                          style={{ width: 220 }}
                          value={clauseManager.clauseForm.clause_scope}
                          onChange={(val) => clauseManager.setClauseForm((f) => ({ ...f, clause_scope: val }))}
                          options={[
                            { label: 'Any (default)', value: 'any' },
                            { label: 'Portfolio holdings', value: 'portfolio' },
                            { label: 'Unknown universe (not in portfolio now)', value: 'unknown' },
                          ]}
                        />
                      </div>
                      <div>
                        <Typography.Text>Trade direction</Typography.Text>
                        <Select
                          style={{ width: 220 }}
                          value={clauseManager.clauseForm.trade_direction}
                          onChange={(val) => clauseManager.setClauseForm((f) => ({ ...f, trade_direction: val }))}
                          options={[
                            { label: 'Buy (default)', value: 'buy' },
                            { label: 'Sell', value: 'sell' },
                            { label: 'Both', value: 'both' },
                          ]}
                        />
                      </div>
                      <div>
                        <Typography.Text>Operator & literal threshold</Typography.Text>
                        <Space>
                          <Select
                            value={clauseManager.clauseForm.clause_operator}
                            onChange={(val) =>
                              clauseManager.setClauseForm((f) => ({ ...f, clause_operator: val }))
                            }
                            options={['<', '<=', '>', '>=', '=', '!='].map((op) => ({ label: op, value: op }))}
                          />
                          <InputNumber
                            value={clauseManager.clauseForm.threshold_value}
                            onChange={(val) =>
                              clauseManager.setClauseForm((f) => ({ ...f, threshold_value: val }))
                            }
                            placeholder="Threshold literal"
                          />
                        </Space>
                      </div>
                      <div>
                        <Typography.Text>Dynamic threshold (optional)</Typography.Text>
                        <Space>
                          <AutoComplete
                            style={{ width: 220 }}
                            options={clauseManager.clauseFieldOptions}
                            allowClear
                            value={clauseManager.clauseForm.threshold_field}
                            filterOption={(inputValue, option) => option?.value?.toLowerCase().includes(String(inputValue || '').toLowerCase())}
                            placeholder="Threshold field"
                            onSelect={(val, option) =>
                              clauseManager.setClauseForm((f) => ({
                                ...f,
                                threshold_field: option.field || val,
                                threshold_sourcetable: option.sourcetable || f.threshold_sourcetable,
                              }))
                            }
                            onChange={(val) =>
                              clauseManager.setClauseForm((f) => ({ ...f, threshold_field: val }))
                            }
                          />
                          <Input
                            style={{ width: 220 }}
                            placeholder="Threshold sourcetable"
                            value={clauseManager.clauseForm.threshold_sourcetable}
                            onChange={(e) =>
                              clauseManager.setClauseForm((f) => ({ ...f, threshold_sourcetable: e.target.value }))
                            }
                          />
                        </Space>
                      </div>
                      <div>
                        <Typography.Text>Explanation / description</Typography.Text>
                        <Input.TextArea
                          rows={3}
                          value={clauseManager.clauseForm.clause_txt}
                          onChange={(e) =>
                            clauseManager.setClauseForm((f) => ({ ...f, clause_txt: e.target.value }))
                          }
                          placeholder="Optional text shown in UI/logs"
                        />
                      </div>
                    </Space>
                  </div>
                </Space>
              ),
            },
            {
              key: 'combinations',
              label: 'Manage Clause Combinations',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Typography.Text strong>Existing combinations</Typography.Text>
                    <Select
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="Select combination to load"
                      value={clauseManager.editingClauseSet?.id || null}
                      loading={clauseManager.clauseSetsLoading}
                      onDropdownVisibleChange={(open) => {
                        if (open) {
                          clauseManager.loadClauseSets();
                        }
                      }}
                      onChange={async (val) => clauseManager.loadClauseSetForEdit(val)}
                      options={clauseManager.clauseSets.map((set) => ({
                        value: set.id,
                        label: `${set.name || `Combination #${set.id}`} (${set.clause_ids?.length || 0} clauses)`,
                      }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Combination details</Typography.Text>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input
                        placeholder="Combination name"
                        value={clauseManager.clauseCombinationForm.name}
                        onChange={(e) =>
                          clauseManager.setClauseCombinationForm((f) => ({ ...f, name: e.target.value }))
                        }
                      />
                      <Input.TextArea
                        rows={3}
                        placeholder="Description (optional)"
                        value={clauseManager.clauseCombinationForm.description}
                        onChange={(e) =>
                          clauseManager.setClauseCombinationForm((f) => ({ ...f, description: e.target.value }))
                        }
                      />
                      <div>
                        <Typography.Text>Clauses included</Typography.Text>
                        <Select
                          mode="multiple"
                          style={{ width: '100%', marginTop: 4 }}
                          placeholder="Select clauses to include"
                          value={clauseManager.clauseCombinationForm.clause_ids}
                          onDropdownVisibleChange={(open) => {
                            if (open && clauseManager.clauseDefinitions.length === 0) {
                              clauseManager.loadClauseDefinitions();
                            }
                          }}
                          onChange={(vals) =>
                            clauseManager.setClauseCombinationForm((f) => ({ ...f, clause_ids: vals }))
                          }
                          options={clauseManager.clauseDefinitions.map((cl) => ({
                            value: cl.id,
                            label: clauseManager.formatClauseLabel(cl),
                          }))}
                        />
                        {clauseManager.clauseCombinationForm.clause_ids.length > 0 && (
                          <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                            {clauseManager.clauseCombinationForm.clause_ids.map((cid) => (
                              <li key={cid}>{clauseManager.formatClauseLabel(clauseManager.clauseDefinitionById[cid]) || `Clause #${cid}`}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </Space>
                  </div>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
      <Drawer
        title="All clauses"
        placement="right"
        width={420}
        open={clauseManager.clauseListDrawerOpen}
        onClose={() => clauseManager.setClauseListDrawerOpen(false)}
        destroyOnClose
      >
        <List
          loading={clauseManager.clauseDefinitionsLoading}
          dataSource={clauseManager.clauseDefinitions}
          bordered
          renderItem={(item) => (
            <List.Item
              key={item.id}
              actions={[
                <Button
                  key="edit"
                  type="link"
                  onClick={async () => {
                    const loaded = await clauseManager.loadClauseForEdit(item.id);
                    if (loaded) {
                      clauseManager.setClauseManagerTab('clauses');
                      clauseManager.setClauseListDrawerOpen(false);
                    }
                  }}
                >
                  Edit
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={clauseManager.formatClauseLabel(item)}
                description={`${(item.clause_field || '').toUpperCase()} ${item.clause_operator || ''} ${
                  item.threshold_value ?? item.threshold_field ?? ''
                }`}
              />
            </List.Item>
          )}
        />
      </Drawer>
      <Drawer
        title="Configure Backtest Run (fictive)"
        width={720}
        open={configOpen}
        onClose={async () => {
          if (editingConfigId) {
            await configManager.saveConfiguration(editingConfigId);
            setEditingConfigId(null);
          }
          setConfigOpen(false);
        }}
        extra={
          <Space>
            <Button
              onClick={async () => {
                const preview = { ...config, start_portfolio_id: selected?.id || config.start_portfolio_id };
                try {
                  await navigator.clipboard.writeText(JSON.stringify(preview, null, 2));
                  message.success('Configuration copied to clipboard');
                } catch {
                  message.info('Select and copy preview manually');
                }
              }}
            >
              Copy JSON
            </Button>
            <Button
              type="primary"
              disabled={!configManager.isDirty}
              onClick={async () => {
                 console.log('[SAVE] Before save - isDirty:', configManager.isDirty);
                 console.log('[SAVE] Before save - editingConfigId:', editingConfigId);
                 console.log('[SAVE] Before save - config.start_portfolio_id:', config.start_portfolio_id);
                 // Call saveConfiguration regardless of editingConfigId
                 // It will handle both new (fictive) and existing configs
                 await configManager.saveConfiguration(editingConfigId);
                 console.log('[SAVE] After save - isDirty:', configManager.isDirty);
                 console.log('[SAVE] After save - editingConfigId:', editingConfigId);
                 console.log('[SAVE] After save - config.start_portfolio_id:', config.start_portfolio_id);
              }}
            >
              Save
            </Button>
            {/* DAS-1703: Prepare for Serial Run button */}
            <Tooltip title={!editingConfigId ? "Save configuration first" : isSerialRunActive ? "Serial run in progress" : "Add to serial run queue"}>
              <Button
                disabled={!editingConfigId || configManager.isDirty || isSerialRunActive}
                onClick={async () => {
                  const res = await callAction('prepare_for_batch', { configuration_id: editingConfigId });
                  if (res?.success) {
                    message.success(`Added to serial run queue (position ${res.data.order_index + 1})`);
                  } else {
                    message.error(res?.error || 'Failed to add to queue');
                  }
                }}
                icon={<UnorderedListOutlined />}
              >
                Queue for Serial
              </Button>
            </Tooltip>
            <Button
              type="primary"
              disabled={configManager.isDirty || !(selected?.id || config.start_portfolio_id) || runStatus === 'running' || isSerialRunActive}
              onClick={async () => {
                console.log('[START RUN] isDirty:', configManager.isDirty);
                console.log('[START RUN] selected?.id:', selected?.id);
                console.log('[START RUN] config.start_portfolio_id:', config.start_portfolio_id);
                console.log('[START RUN] runStatus:', runStatus);
                console.log('[START RUN] DISABLED:', configManager.isDirty || !(selected?.id || config.start_portfolio_id) || runStatus === 'running');
                setConfigOpen(false);
                await runManager.onRun();
              }}
              loading={runStatus === 'running'}
              style={{ backgroundColor: (configManager.isDirty || !(selected?.id || config.start_portfolio_id)) ? undefined : '#52c41a', borderColor: (configManager.isDirty || !(selected?.id || config.start_portfolio_id)) ? undefined : '#52c41a' }}
            >
              {runStatus === 'running' ? 'Running...' : 'Start Run'}
            </Button>
            <Button
              onClick={() => {
                setConfigOpen(false);
                setEditingConfigId(null);
              }}
            >
              Close
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {configValidationErrors.length > 0 && (
            <div style={{ padding: 12, background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4 }}>
              <Typography.Text strong style={{ color: '#d4380d' }}>Configuration Incomplete</Typography.Text>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                {configValidationErrors.map((err, idx) => (
                  <li key={idx} style={{ color: '#d4380d' }}>{err}</li>
                ))}
              </ul>
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                Please complete the missing fields before creating a run.
              </div>
            </div>
          )}
          <ConfigurationForm
            config={config}
            setConfig={setConfig}
            handleStartDateChange={configManager.handleStartDateChange}
            handleEndDateChange={configManager.handleEndDateChange}
            applyEndPreset={configManager.applyEndPreset}
            handleRebalancingToggle={configManager.handleRebalancingToggle}
            signalSource={signalSource}
            handleSignalSourceChange={configManager.handleSignalSourceChange}
            clauseDefinitionsLoading={clauseManager.clauseDefinitionsLoading}
            loadClauseDefinitions={clauseManager.loadClauseDefinitions}
            clauseDefinitions={clauseManager.clauseDefinitions}
            formatClauseLabel={clauseManager.formatClauseLabel}
            ensureClauseDataLoaded={clauseManager.ensureClauseDataLoaded}
            ensureClauseSetsLoaded={clauseManager.ensureClauseSetsLoaded}
            setClauseManagerTab={clauseManager.setClauseManagerTab}
            setEditingClause={clauseManager.setEditingClause}
            setClauseForm={clauseManager.setClauseForm}
            setEditingClauseSet={clauseManager.setEditingClauseSet}
            setClauseCombinationForm={clauseManager.setClauseCombinationForm}
            setClauseModalOpen={clauseManager.setClauseModalOpen}
            clauseSetsLoading={clauseManager.clauseSetsLoading}
            loadClauseSets={clauseManager.loadClauseSets}
            clauseSets={clauseManager.clauseSets}
            updateClauseCooldownDefault={configManager.updateClauseCooldownDefault}
            selectedClauseRows={selectedClauseRows}
            selectedClauseSetRows={selectedClauseSetRows}
            renderClauseCooldownRow={renderClauseCooldownRow}
            renderClausePositionSizingRow={renderClausePositionSizingRow}
            portfolioCashEur={portfolioCashEur}
            selected={selected}
            openDetails={openDetails}
          />
        </Space>
      </Drawer>
      <AlertSelectionModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        selectedIds={config.alert_ids}
        onSelectionChange={(ids) => setConfig((c) => ({ ...c, alert_ids: ids }))}
        singleSelect={config.scenario === 'Simple test 1 alert'}
        callAction={callAction}
      />
      <Drawer
        title={`Run #${runInfo?.run_id || ''} Results`}
        width={1000}
        open={resultsManager.resultsOpen}
        onClose={() => resultsManager.setResultsOpen(false)}
      >
        <ResultsDrawer
          runInfo={runInfo}
          resultsData={resultsManager.resultsData}
          warningsData={resultsManager.warningsData}
          exportResultsCsv={resultsManager.exportResultsCsv}
          copyResultsJson={resultsManager.copyResultsJson}
          downloadResultsJson={resultsManager.downloadResultsJson}
          downloadPricesAuditJson={resultsManager.downloadPricesAuditJson}
          copyRunConfigJson={resultsManager.copyRunConfigJson}
          downloadRunConfigJson={resultsManager.downloadRunConfigJson}
          createRunJiraTicket={resultsManager.createRunJiraTicket}
          universeJson={resultsManager.universeJson}
          setUniverseOpen={resultsManager.setUniverseOpen}
          eqCheck={resultsManager.eqCheck}
          twrSummary={resultsManager.twrSummary}
          callAction={callAction}
          setEqCheck={resultsManager.setEqCheck}
          setEqCheckOpen={resultsManager.setEqCheckOpen}
          portfolioSummary={resultsManager.portfolioSummary}
          cashData={resultsManager.chartData.cashData}
          cashTransactions={resultsManager.chartData.cashTransactions}
          overlayData={resultsManager.chartData.overlayData}
          equityData={resultsManager.chartData.equityData}
          benchmarkData={resultsManager.chartData.benchmarkData}
          secondaryBenchmarkData={resultsManager.chartData.secondaryBenchmarkData}
          excessData={resultsManager.chartData.excessData}
          excessLineColor={resultsManager.chartData.excessLineColor}
          excessTransactions={resultsManager.chartData.excessTransactions}
          fxSummary={resultsManager.resultsData?.fx_summary}
          loadPricesDebugData={resultsManager.loadPricesDebugData}
          pricesDebugLoading={resultsManager.pricesDebugLoading}
          pricesDebugData={resultsManager.pricesDebugData}
          selectedDebugDate={resultsManager.selectedDebugDate}
          setSelectedDebugDate={resultsManager.setSelectedDebugDate}
          selectedDateDebugData={resultsManager.selectedDateDebugData}
          eventsList={resultsManager.chartData.eventsList}
          performanceMetrics={resultsManager.chartData.performanceMetrics} // Pass this new prop
          tradeTimelineModal={resultsManager.tradeTimelineModal}
          openTradeTimeline={resultsManager.openTradeTimeline}
          closeTradeTimeline={resultsManager.closeTradeTimeline}
          copyTradeTimelineJson={resultsManager.copyTradeTimelineJson}
          createTradeTimelineJiraTicket={resultsManager.createTradeTimelineJiraTicket}
          alertDetailModal={resultsManager.alertDetailModal}
          openAlertDetails={resultsManager.openAlertDetails}
          closeAlertDetails={resultsManager.closeAlertDetails}
          clauseDetailModal={resultsManager.clauseDetailModal}
          openClauseDetails={resultsManager.openClauseDetails}
          closeClauseDetails={resultsManager.closeClauseDetails}
          rebalancingModal={resultsManager.rebalancingModal}
          openRebalancingDetails={resultsManager.openRebalancingDetails}
          closeRebalancingDetails={resultsManager.closeRebalancingDetails}
          onOpenConfiguration={async () => {
            // DAS-1683: Open configuration for editing from results drawer
            // DAS-1860: Use portfolio snapshot to show historical state, not current
            const configId = runInfo?.config_id || runInfo?.configuration_id || resultsManager.resultsData?.config?.id;
            if (configId) {
              await configManager.loadConfiguration(configId, startPortfolios, setSelected);
              
              // DAS-1860: If there's a snapshot, use it instead of current portfolio
              const snapshot = resultsManager.resultsData?.start_portfolio_snapshot;
              if (snapshot) {
                // Create a synthetic portfolio from the snapshot
                const snapshotPortfolio = {
                  id: snapshot.id || resultsManager.resultsData?.config?.start_portfolio_id,
                  name: `📸 ${snapshot.name || 'Snapshot'} (at run time)`,
                  holdings: snapshot.holdings || [],
                  cash_by_currency: snapshot.cash_by_currency || { EUR: 0 },
                  isSnapshot: true,  // Flag to indicate this is historical
                  snapshotRunId: runInfo?.run_id,
                };
                setSelected(snapshotPortfolio);
                message.info('Showing portfolio state at run time (snapshot)');
              }
              
              resultsManager.setResultsOpen(false); // Close results drawer
              setConfigOpen(true); // Open config drawer
            } else {
              message.warning('No configuration ID found for this run');
            }
          }}
        />
      </Drawer>
      <EquityCheckModal
        open={resultsManager.eqCheckOpen}
        onClose={() => resultsManager.setEqCheckOpen(false)}
        eqCheck={resultsManager.eqCheck}
        runInfo={runInfo}
        config={config}
        onCreateTicket={resultsManager.createRunJiraTicket}
        callAction={callAction}
        onRefresh={resultsManager.refreshEquityCheck}  // DAS-1748: Force refresh
      />
      <Modal
        title="Universe Debug"
        open={resultsManager.universeOpen}
        onCancel={() => resultsManager.setUniverseOpen(false)}
        width={800}
        footer={null}
      >
        {resultsManager.universeList.length > 0 ? (
          <Table
            dataSource={resultsManager.universeList}
            rowKey="ticker"
            size="small"
            pagination={{ pageSize: 20 }}
            columns={[
              { title: 'Ticker', dataIndex: 'ticker', key: 'ticker' },
              { title: 'Status', dataIndex: 'status', key: 'status' },
              { title: 'Reason', dataIndex: 'reason', key: 'reason' },
              { title: 'Market Cap', dataIndex: 'market_cap', key: 'market_cap', render: (v) => v != null ? Number(v).toLocaleString() : '-' },
              { title: 'Avg Vol', dataIndex: 'avg_vol', key: 'avg_vol', render: (v) => v != null ? Number(v).toLocaleString() : '-' },
            ]}
          />
        ) : (
          <pre style={{ maxHeight: 400, overflow: 'auto' }}>{resultsManager.universeJson || 'No data'}</pre>
        )}
      </Modal>
    </div>
  );
};

export default Backtesting;
